import { useCallback, useSyncExternalStore } from 'react'
import { wsClient, type WsClient } from '@/shared/ws'
import { ConnectionStatus, type ConnectionState } from './ConnectionStatus'

/**
 * Состояние канала для шапки: открыт сокет — на связи; сети нет (`navigator.onLine`) — офлайн;
 * иначе клиент переподключается. Статус `idle`/`connecting`/`closed` пользователю неинтересен —
 * ему важно лишь «данные идут / не идут», поэтому три состояния вместо четырёх.
 */
function snapshotOf(client: WsClient): ConnectionState {
  if (client.getStatus() === 'open') return 'online'
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'offline'
  return 'reconnecting'
}

/**
 * Контейнер индикатора: слушает `wsClient.onStatusChange` и события сети. Реального соединения
 * в тестах нет — клиент подменяется пропом `client`.
 */
export function ConnectionIndicator({ client = wsClient }: { client?: WsClient }) {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const off = client.onStatusChange(onChange)
      window.addEventListener('online', onChange)
      window.addEventListener('offline', onChange)
      return () => {
        off()
        window.removeEventListener('online', onChange)
        window.removeEventListener('offline', onChange)
      }
    },
    [client],
  )

  const getSnapshot = useCallback(() => snapshotOf(client), [client])
  const state = useSyncExternalStore(subscribe, getSnapshot, () => 'reconnecting' as ConnectionState)

  return <ConnectionStatus state={state} />
}
